import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Banner } from '@/components/ui/Banner';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';

export interface PatientTabEmptyCopy {
  icon: ReactNode;
  title: ReactNode;
  description?: ReactNode;
}

export interface PatientTabFrameProps {
  title: ReactNode;
  count?: number;
  action?: ReactNode;
  toolbar?: ReactNode;
  banner?: ReactNode;
  loading?: boolean;
  error?: ReactNode;
  onRetry?: () => void;
  empty?: PatientTabEmptyCopy | false | null;
  children?: ReactNode;
  className?: string;
  contentClassName?: string;
  /** Journal: fill the remaining page height. */
  fill?: boolean;
  headingId?: string;
}

/**
 * Shared staff patient-detail tab chrome (Designmanual V2 note 07).
 * One full-width card, title + count + one header action, optional toolbar,
 * and list/table/timeline content. Empty states never include a centred button.
 */
export function PatientTabFrame({
  title,
  count,
  action,
  toolbar,
  banner,
  loading = false,
  error,
  onRetry,
  empty,
  children,
  className,
  contentClassName,
  fill = false,
  headingId,
}: PatientTabFrameProps) {
  const { t } = useTranslation();
  const showEmpty = Boolean(empty) && !loading && !error;

  return (
    <Card
      className={cn(fill && 'flex min-h-0 flex-1 flex-col overflow-hidden', className)}
      aria-busy={loading || undefined}
    >
      <div className="flex min-h-10 flex-wrap items-center gap-s3">
        <h2
          id={headingId}
          className={cn(TYPE.heading, 'shrink-0 text-ds-text')}
        >
          {title}
        </h2>
        {count != null ? (
          <span
            className={cn(
              TYPE.meta,
              'inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-ds-neutral-tint px-s2 text-ds-muted',
            )}
          >
            {count}
          </span>
        ) : null}
        {action ? <div className="ml-auto shrink-0">{action}</div> : null}
      </div>

      {error ? (
        <Banner
          className="mt-s4"
          tone="error"
          title={error}
          action={
            onRetry ? (
              <Button type="button" variant="secondary" onClick={onRetry}>
                {t('app.retry')}
              </Button>
            ) : undefined
          }
        />
      ) : null}

      {banner && !error ? <div className="mt-s4">{banner}</div> : null}

      {toolbar && !loading && !error ? toolbar : null}

      <div className={cn(fill && 'flex min-h-0 min-w-0 flex-1 flex-col', contentClassName)}>
        {loading ? (
          <div className="mt-s4 flex flex-col gap-s3" aria-hidden>
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : null}

        {showEmpty && empty ? (
          <PatientTabEmpty
            icon={empty.icon}
            title={empty.title}
            description={empty.description}
          />
        ) : null}

        {!loading && !error && !showEmpty ? children : null}
      </div>
    </Card>
  );
}

export function PatientTabToolbar({
  chips,
  search,
  filters,
  className,
}: {
  chips?: ReactNode;
  search?: ReactNode;
  filters?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'mt-s4 flex flex-col gap-s3 nav:flex-row nav:items-center nav:justify-between',
        className,
      )}
    >
      {chips ? (
        <div className="min-w-0 overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
          <div className="flex w-max gap-s2 nav:w-auto nav:flex-wrap">{chips}</div>
        </div>
      ) : (
        <span />
      )}
      {search || filters ? (
        <div className="flex w-full shrink-0 flex-col gap-s2 nav:w-auto nav:flex-row nav:items-center">
          {search ? <div className="w-full nav:w-[240px]">{search}</div> : null}
          {filters}
        </div>
      ) : null}
    </div>
  );
}

export function PatientTabEmpty({
  icon,
  title,
  description,
}: PatientTabEmptyCopy) {
  return (
    <div className="flex flex-col items-center px-s4 py-s7 text-center">
      <div
        className="mb-s4 flex h-16 w-16 items-center justify-center rounded-full bg-ds-teal-tint text-ds-teal"
        aria-hidden
      >
        {icon}
      </div>
      <p className={cn(TYPE.section, 'text-ds-text')}>{title}</p>
      {description ? (
        <p className={cn(TYPE.body, 'mt-s2 max-w-[480px] text-ds-muted')}>{description}</p>
      ) : null}
    </div>
  );
}

export function PatientTabSection({
  title,
  meta,
  action,
  children,
  className,
}: {
  title: ReactNode;
  meta?: ReactNode;
  action?: ReactNode;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn('border-t border-ds-line pt-s4 first:border-t-0 first:pt-0', className)}>
      <div className="mb-s3 flex flex-wrap items-center gap-s2">
        <h3 className={cn(TYPE.label, 'text-ds-muted')}>{title}</h3>
        {meta}
        {action ? <div className="ml-auto shrink-0">{action}</div> : null}
      </div>
      {children}
    </section>
  );
}
